import * as THREE from 'three';
import { BLOCK_CAPS, glowSpots, isShapeKind, type Lesson } from '@vrlearning/lesson-schema';
import { shown } from '../motion.js';
import { softDisc } from './noise.js';

/**
 * Glow halos, not bloom (ADR-032). Bloom is several full-screen passes per
 * eye, too much for a Quest at 72 Hz; a soft additive quad on each glowing
 * part says "this is hot, this gives light" for one draw call in all. The
 * quads are one instanced mesh, turned to the viewer on the CPU each frame,
 * depth-tested and fogged like everything else.
 */

/** A halo is this many times the part's radius across... */
export const HALO_SCALE = 2.6;
/** ...but never smaller or bigger than this. */
export const HALO_MIN = 0.12;
export const HALO_MAX = 3;
export const HALO_OPACITY = 0.45;
/** How much brighter a halo is at a highlight's peak. */
export const HALO_HIGHLIGHT = 1.8;
export const HALOS_PER_LESSON = 32;
/** One number with the world checker, so a package that passes is never trimmed. */
export const HALOS_PER_WORLD = BLOCK_CAPS.halos;

export type HaloSource = {
  /** What the halo sits on: its centre is in this object's own space. */
  readonly on: THREE.Object3D;
  readonly centre: THREE.Vector3;
  /** Metres, in `on`'s space: scaled with it. */
  readonly radius: number;
  readonly colour: THREE.Color;
  /** The glowing material, read for a highlight: brighter when its emissive is. */
  readonly material?: THREE.MeshStandardMaterial;
  /** Its emissive intensity at rest. */
  readonly rest?: number;
  /** For a world's flame or crater: brightness over time, around 1. */
  readonly flicker?: (elapsed: number) => number;
};

/**
 * A lesson's halos: one per glowing part (up to four along a long one), for
 * each shape built, at most 32.
 */
export function lessonHalos(
  lesson: Lesson,
  bodies: ReadonlyMap<string, THREE.Mesh>,
  max = HALOS_PER_LESSON,
): HaloSource[] {
  const sources: HaloSource[] = [];
  for (const [id, object] of Object.entries(lesson.objects ?? {})) {
    if (!isShapeKind(object.kind) || !object.parts) continue;
    const body = bodies.get(id);
    if (!body) continue;
    const materials = (Array.isArray(body.material) ? body.material : [body.material]).filter(
      (material): material is THREE.MeshStandardMaterial =>
        material instanceof THREE.MeshStandardMaterial && material.userData['finish'] === 'glow',
    );
    for (const spot of glowSpots(object.parts)) {
      const material = materials.find(
        (candidate) => `#${candidate.emissive.getHexString()}` === spot.colour,
      );
      sources.push({
        on: body,
        centre: new THREE.Vector3(...spot.centre),
        radius: spot.radius,
        colour: new THREE.Color(spot.colour),
        ...(material ? { material, rest: material.emissiveIntensity } : {}),
      });
      if (sources.length >= max) return sources;
    }
  }
  return sources;
}

let disc: THREE.DataTexture | undefined;

export class HaloField {
  readonly mesh: THREE.InstancedMesh;
  readonly #sources: readonly HaloSource[];
  readonly #matrix = new THREE.Matrix4();
  readonly #position = new THREE.Vector3();
  readonly #scale = new THREE.Vector3();
  readonly #turn = new THREE.Quaternion();
  readonly #colour = new THREE.Color();
  /** The largest a halo gets, metres: 3 for a lesson's; a world's crater far off may be bigger. */
  maxSize = HALO_MAX;

  /**
   * @param additive Light added to what is behind: a lesson's glow. A world's
   *   far-off glow is blended instead, or against a bright sky it adds up to
   *   white dots rather than a warm haze.
   */
  constructor(sources: readonly HaloSource[], name = 'halos', additive = true) {
    this.#sources = sources;
    disc ??= softDisc(64, 2.0);
    const material = new THREE.MeshBasicMaterial({
      map: disc,
      transparent: true,
      opacity: HALO_OPACITY,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      depthWrite: false,
      fog: true,
    });
    this.mesh = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      material,
      Math.max(1, sources.length),
    );
    this.mesh.name = name;
    this.mesh.count = sources.length;
    // Moved every frame, so never culled by a stale bounding sphere; never a target.
    this.mesh.frustumCulled = false;
    this.mesh.raycast = (): void => {};
    this.mesh.renderOrder = 5;
    this.mesh.visible = sources.length > 0;
    for (let i = 0; i < sources.length; i++) this.mesh.setColorAt(i, sources[i]!.colour);
  }

  get count(): number {
    return this.#sources.length;
  }

  /** Follows show, hide, move, scale and highlight, and turns every quad to the viewer. */
  update(camera: THREE.Camera, elapsed = 0): void {
    if (this.#sources.length === 0) return;
    camera.getWorldQuaternion(this.#turn);
    for (let i = 0; i < this.#sources.length; i++) {
      const source = this.#sources[i]!;
      let size = 0;
      if (shown(source.on)) {
        source.on.updateWorldMatrix(true, false);
        this.#position.copy(source.centre).applyMatrix4(source.on.matrixWorld);
        this.#scale.setFromMatrixScale(source.on.matrixWorld);
        size = THREE.MathUtils.clamp(
          source.radius * HALO_SCALE * Math.max(this.#scale.x, this.#scale.y, this.#scale.z),
          HALO_MIN,
          this.maxSize,
        );
        // Shrunk to nothing, a growing or vanishing object takes its halo with it.
        size *= Math.min(1, Math.max(this.#scale.x, this.#scale.y, this.#scale.z) * 4);
      }
      this.#matrix.compose(this.#position, this.#turn, this.#scale.set(size, size, size));
      this.mesh.setMatrixAt(i, this.#matrix);
      this.#colour.copy(source.colour).multiplyScalar(this.brightness(source, elapsed));
      this.mesh.setColorAt(i, this.#colour);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  /** 1 at rest, up to 1.8 at a highlight's peak, times a flame's flicker. */
  brightness(source: HaloSource, elapsed = 0): number {
    let factor = 1;
    if (source.material && source.rest) {
      const over = source.material.emissiveIntensity / source.rest - 1;
      factor = 1 + (HALO_HIGHLIGHT - 1) * THREE.MathUtils.clamp(over, 0, 1);
    }
    return factor * (source.flicker?.(elapsed) ?? 1);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
    this.mesh.removeFromParent();
  }
}
